import { apiClient } from "./api";

const EQUIPMENT_BASE = "/equipment";

export const EQUIPMENT_DEFAULT_PAGE_SIZE = 20;
export const EQUIPMENT_MAX_PAGE_SIZE = 100;
export const EQUIPMENT_DEFAULT_SORT_BY = "updatedAtUtc";
export const EQUIPMENT_DEFAULT_SORT_DIRECTION = "desc";

/**
 * The three date reminder cards are counted over the actionable set only
 * (未归档 且 available/in_use). Any list opened from one of those cards must send this scope,
 * otherwise the card number and the list total disagree.
 */
export const EQUIPMENT_REMINDER_SCOPE_ACTIONABLE = "actionable";

const toDisplay = (options) =>
    options.reduce((display, option) => {
        display[option.value] = option.label;
        return display;
    }, {});

// Stable codes shared with the backend; never invent new ones here.
export const equipmentTypeOptions = [
    { value: "drone", label: "无人机" },
    { value: "camera", label: "相机" },
    { value: "lens", label: "镜头" },
    { value: "battery", label: "电池" },
    { value: "lidar", label: "雷达" },
    { value: "remote_controller", label: "遥控器" },
    { value: "rtk", label: "RTK 设备" },
    { value: "transport_case", label: "运输箱" },
    { value: "other", label: "其他" },
];

export const equipmentOperationalStatusOptions = [
    { value: "available", label: "可用" },
    { value: "in_use", label: "使用中" },
    { value: "under_service", label: "维修保养中" },
    { value: "out_of_service", label: "停用" },
    { value: "retired", label: "退役" },
    { value: "scrapped", label: "报废" },
];

export const equipmentWarrantyStateOptions = [
    { value: "not_set", label: "未设置" },
    { value: "expired", label: "已过期" },
    { value: "due_soon", label: "即将到期" },
    { value: "valid", label: "有效" },
];

export const equipmentMaintenanceStateOptions = [
    { value: "not_set", label: "未设置" },
    { value: "overdue", label: "已逾期" },
    { value: "due_soon", label: "即将到期" },
    { value: "normal", label: "正常" },
];

export const serviceTypeOptions = [
    { value: "maintenance", label: "保养" },
    { value: "repair", label: "维修" },
    { value: "inspection", label: "检查" },
    { value: "calibration", label: "校准" },
];

export const serviceStatusOptions = [
    { value: "draft", label: "草稿" },
    { value: "in_progress", label: "处理中" },
    { value: "completed", label: "已完成" },
    { value: "cancelled", label: "已取消" },
];

/** Accepted `UsageValueAtService` units; the server validates against the same four codes. */
export const serviceUsageUnitOptions = [
    { value: "flight_hour", label: "飞行小时" },
    { value: "cycle", label: "循环次数" },
    { value: "shutter_count", label: "快门数" },
    { value: "other", label: "其他" },
];

// Sort fields accepted by GET /equipment/service-records.
export const serviceRecordSortOptions = [
    // Design 8.4: by when the work happened, with anything still open pinned on top. The default
    // on a single unit's timeline, because back-filling an old repair must not bury today's
    // open order under it.
    { value: "timeline", label: "时间线（进行中置顶）" },
    { value: "createdAtUtc", label: "创建时间" },
    { value: "updatedAtUtc", label: "更新时间" },
    { value: "recordNo", label: "记录编号" },
    { value: "serviceType", label: "类型" },
    { value: "status", label: "状态" },
    { value: "reportedDate", label: "报告日期" },
    { value: "startedDate", label: "开始日期" },
    { value: "completedDate", label: "完成日期" },
];

/**
 * Lifecycle action codes carried per row in `allowedActions`.
 * <p>
 * Rendered verbatim. The server computes them from the record's status, the caller's write
 * permission and the admin-only void, so the page must not re-derive any of that — a client-side
 * copy drifts and the symptom is a menu whose entries answer 403 or 409.
 */
export const serviceRecordActions = {
    edit: "edit",
    start: "start",
    complete: "complete",
    cancel: "cancel",
    void: "void",
};

// Sort fields accepted by GET /equipment; anything else is rejected with InvalidSortField.
export const equipmentSortOptions = [
    { value: "updatedAtUtc", label: "更新时间" },
    { value: "assetCode", label: "资产编号" },
    { value: "name", label: "名称" },
    { value: "type", label: "类型" },
    { value: "operationalStatus", label: "运行状态" },
    { value: "nextMaintenanceDate", label: "下次保养" },
    { value: "warrantyEndDate", label: "保修截止" },
    { value: "createdAtUtc", label: "创建时间" },
];

export const equipmentTypeDisplay = toDisplay(equipmentTypeOptions);
export const equipmentOperationalStatusDisplay = toDisplay(equipmentOperationalStatusOptions);
export const equipmentWarrantyStateDisplay = toDisplay(equipmentWarrantyStateOptions);
export const equipmentMaintenanceStateDisplay = toDisplay(equipmentMaintenanceStateOptions);
export const serviceTypeDisplay = toDisplay(serviceTypeOptions);
export const serviceStatusDisplay = toDisplay(serviceStatusOptions);
export const serviceUsageUnitDisplay = toDisplay(serviceUsageUnitOptions);

export const serviceTypeLabel = (code) => serviceTypeDisplay[code] || code || "";
export const serviceStatusLabel = (code) => serviceStatusDisplay[code] || code || "";
export const serviceUsageUnitLabel = (code) => serviceUsageUnitDisplay[code] || code || "";

export const equipmentTypeLabel = (code) => equipmentTypeDisplay[code] || code || "";
export const equipmentOperationalStatusLabel = (code) =>
    equipmentOperationalStatusDisplay[code] || code || "";
export const equipmentWarrantyStateLabel = (code) => equipmentWarrantyStateDisplay[code] || code || "";
export const equipmentMaintenanceStateLabel = (code) =>
    equipmentMaintenanceStateDisplay[code] || code || "";

/** Stable error codes returned as `errorCode` in the API error body. */
export const equipmentErrorCodes = {
    RequestValidationFailed: "RequestValidationFailed",
    VersionConflict: "VersionConflict",
    ConcurrentMutation: "ConcurrentMutation",
    AssetCodeDuplicate: "AssetCodeDuplicate",
    SerialNumberDuplicate: "SerialNumberDuplicate",
    DateAnomalyRequiresConfirmation: "DateAnomalyRequiresConfirmation",
    TenantForbidden: "TenantForbidden",
    TenantScopeRequired: "TenantScopeRequired",
    TenantRequired: "TenantRequired",
    CustodianInvalid: "CustodianInvalid",
    InvalidSpecifications: "InvalidSpecifications",
    InvalidDateRange: "InvalidDateRange",
    InvalidMaintenanceInterval: "InvalidMaintenanceInterval",
    EquipmentNotFound: "EquipmentNotFound",
    ArchiveNotAllowed: "ArchiveNotAllowed",
    AlreadyArchived: "AlreadyArchived",
    NotArchived: "NotArchived",
    // service records
    ServiceRecordNotFound: "ServiceRecordNotFound",
    ServiceRecordNotEditable: "ServiceRecordNotEditable",
    ServiceRecordStateConflict: "ServiceRecordStateConflict",
    ServiceRecordInProgressExists: "ServiceRecordInProgressExists",
    ServiceRecordNotTerminal: "ServiceRecordNotTerminal",
    ServiceRecordAlreadyVoided: "ServiceRecordAlreadyVoided",
    EquipmentInUse: "EquipmentInUse",
    EquipmentNotServiceable: "EquipmentNotServiceable",
    ResultingStatusNotAllowed: "ResultingStatusNotAllowed",
    CompletedDateInFuture: "CompletedDateInFuture",
    InvalidCurrency: "InvalidCurrency",
    InvalidCost: "InvalidCost",
    InvalidUsageUnit: "InvalidUsageUnit",
    ReasonRequired: "ReasonRequired",
    AdminRequired: "AdminRequired",
    InvalidStatusTransition: "InvalidStatusTransition",
    TenantNotFound: "TenantNotFound",
    // session equipment
    SessionNotFound: "SessionNotFound",
    SessionConcluded: "SessionConcluded",
    SessionSetupNotRequiredBlocked: "SessionSetupNotRequiredBlocked",
    DuplicateEquipmentInRequest: "DuplicateEquipmentInRequest",
    InvalidUsageRole: "InvalidUsageRole",
    EquipmentNotSelectable: "EquipmentNotSelectable",
    OccupancyNotActive: "OccupancyNotActive",
    MaintenanceOverrideReasonRequired: "MaintenanceOverrideReasonRequired",
    MaintenanceOverrideForbidden: "MaintenanceOverrideForbidden",
    ReassignmentRequiresConfirmation: "ReassignmentRequiresConfirmation",
};

/** Errors whose message alone is not actionable enough for the operator. */
const errorCodeHints = {
    [equipmentErrorCodes.VersionConflict]: "记录已被其他用户修改，请重新加载后再试。",
    [equipmentErrorCodes.ConcurrentMutation]: "数据正在被其他操作修改，请重新加载后重试。",
    [equipmentErrorCodes.TenantForbidden]: "没有访问该租户器材的权限。",
    [equipmentErrorCodes.TenantScopeRequired]: "当前账号没有可用的租户作用域，无法执行写操作。",
    [equipmentErrorCodes.ServiceRecordInProgressExists]:
        "该器材已有进行中的维保记录，请先完成或取消它。",
    [equipmentErrorCodes.EquipmentInUse]: "器材正被巡检会话占用，请先结束占用再送修。",
    [equipmentErrorCodes.ServiceRecordNotEditable]:
        "已完成、已取消或已作废的记录不能再编辑；如需更正请由管理员作废后重建。",
    [equipmentErrorCodes.SessionConcluded]: "该巡检会话已发布，器材组合只能由管理员更正并填写原因。",
    [equipmentErrorCodes.SessionSetupNotRequiredBlocked]:
        "该会话已有器材使用记录，不能标记为无需器材。",
    [equipmentErrorCodes.MaintenanceOverrideForbidden]:
        "保养逾期的器材仅管理员可在填写原因后选择。",
    [equipmentErrorCodes.OccupancyNotActive]: "指定的器材当前不在本会话的活跃占用中，请刷新后重试。",
};

export const equipmentErrorCode = (error) => error?.errorCode || null;

export const equipmentErrorMessage = (error) => {
    const code = equipmentErrorCode(error);
    return error?.message || errorCodeHints[code] || "操作失败";
};

/** Support-facing suffix; empty when the backend did not return a trace id. */
export const equipmentErrorTrace = (error) => (error?.traceId ? `追踪号 ${error.traceId}` : "");

export const isEquipmentErrorCode = (error, code) => equipmentErrorCode(error) === code;

// --------------------------------------------------------------- permissions

const groupLookup = {
    admin: "Admin",
    qa: "QA",
    fieldoperator: "FieldOperator",
    field_operator: "FieldOperator",
    "field-admin": "FieldOperator",
    field_admin: "FieldOperator",
};

const resolveGroups = (user) => {
    const raw = Array.isArray(user?.groups) && user.groups.length ? user.groups : [user?.role];
    return Array.from(
        new Set(
            raw
                .map((value) => (value ? groupLookup[String(value).toLowerCase()] : null))
                .filter(Boolean)
        )
    );
};

/**
 * Which equipment buttons to render. This is UX only — the backend `EquipmentRead/Write/Admin`
 * policies and the tenant filter are the actual security boundary.
 */
export const resolveEquipmentPermissions = (user) => {
    const groups = resolveGroups(user);
    const isAdmin = groups.includes("Admin");
    const isFieldOperator = groups.includes("FieldOperator");
    const isQA = groups.includes("QA");
    return {
        isAdmin,
        canRead: isAdmin || isFieldOperator || isQA,
        canWrite: isAdmin || isFieldOperator,
        canAdmin: isAdmin,
    };
};

// --------------------------------------------------------------------- dates

/** `Date` -> `yyyy-MM-dd` in local time, the wire format for the backend `DateOnly` fields. */
export const toDateOnly = (value) => {
    if (!value) {
        return null;
    }
    if (typeof value === "string") {
        return value.slice(0, 10);
    }
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, "0");
    const day = `${value.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
};

/**
 * `yyyy-MM-dd` -> local `Date`. Parsed field by field on purpose: `new Date("2026-01-10")`
 * is UTC midnight and shifts to the previous day in negative-offset zones.
 */
export const parseDateOnly = (text) => {
    if (!text) {
        return null;
    }
    const [year, month, day] = String(text).slice(0, 10).split("-").map(Number);
    if (!year || !month || !day) {
        return null;
    }
    return new Date(year, month - 1, day);
};

export const formatDateOnly = (text) => (text ? String(text).slice(0, 10) : "");

// ------------------------------------------------------------------- queries

const cleanParams = (params) => {
    const cleaned = {};
    Object.entries(params || {}).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") {
            return;
        }
        cleaned[key] = value;
    });
    return cleaned;
};

/**
 * Builds the server-side query. Filtering, sorting and paging are always done by the API:
 * the page never trims the returned rows, otherwise the totals stop matching.
 */
export const buildEquipmentListParams = (query = {}) =>
    cleanParams({
        tenantId: query.tenantId,
        keyword: query.keyword,
        type: query.type,
        operationalStatus: query.operationalStatus,
        maintenanceState: query.maintenanceState,
        warrantyState: query.warrantyState,
        custodianUserId: query.custodianUserId,
        includeArchived: query.includeArchived ? true : undefined,
        reminderScope: query.reminderScope,
        page: query.page || 1,
        pageSize: query.pageSize || EQUIPMENT_DEFAULT_PAGE_SIZE,
        sortBy: query.sortBy || EQUIPMENT_DEFAULT_SORT_BY,
        sortDirection: query.sortDirection || EQUIPMENT_DEFAULT_SORT_DIRECTION,
    });

export const listEquipment = async (query = {}) => {
    const result = await apiClient.get(EQUIPMENT_BASE, { params: buildEquipmentListParams(query) });
    return {
        items: result?.items || [],
        page: result?.page || 1,
        pageSize: result?.pageSize || EQUIPMENT_DEFAULT_PAGE_SIZE,
        totalCount: result?.totalCount || 0,
        totalPages: result?.totalPages || 0,
    };
};

export const getEquipment = async (id) => apiClient.get(`${EQUIPMENT_BASE}/${id}`);

export const getEquipmentOptions = async (tenantId) => {
    const items = await apiClient.get(`${EQUIPMENT_BASE}/options`, {
        params: cleanParams({ tenantId }),
    });
    return items || [];
};

export const getEquipmentReminderSummary = async (tenantId) =>
    apiClient.get(`${EQUIPMENT_BASE}/reminders/summary`, { params: cleanParams({ tenantId }) });

// -------------------------------------------------------------------- writes

export const createEquipment = async (payload) => apiClient.post(EQUIPMENT_BASE, payload);

export const updateEquipment = async (id, payload) => apiClient.put(`${EQUIPMENT_BASE}/${id}`, payload);

export const changeEquipmentStatus = async (id, { operationalStatus, reason }) =>
    apiClient.post(`${EQUIPMENT_BASE}/${id}/status`, { operationalStatus, reason });

export const archiveEquipment = async (id, reason) =>
    apiClient.post(`${EQUIPMENT_BASE}/${id}/archive`, { reason });

export const restoreEquipment = async (id, reason) =>
    apiClient.post(`${EQUIPMENT_BASE}/${id}/restore`, { reason });

export const listMaintenanceScheduleAdjustments = async (id) => {
    const items = await apiClient.get(`${EQUIPMENT_BASE}/${id}/maintenance-schedule-adjustments`);
    return items || [];
};

export const createMaintenanceScheduleAdjustment = async (id, payload) =>
    apiClient.post(`${EQUIPMENT_BASE}/${id}/maintenance-schedule-adjustments`, payload);

export const voidMaintenanceScheduleAdjustment = async (id, adjustmentId, reason) =>
    apiClient.post(
        `${EQUIPMENT_BASE}/${id}/maintenance-schedule-adjustments/${adjustmentId}/void`,
        { reason }
    );

// ----------------------------------------------------------- service records

export const buildServiceRecordListParams = (query = {}) =>
    cleanParams({
        tenantId: query.tenantId,
        // Honoured only on the flat route; the nested one takes the equipment from the path.
        equipmentId: query.equipmentId,
        serviceType: query.serviceType,
        status: query.status,
        includeVoided: query.includeVoided ? true : undefined,
        keyword: query.keyword,
        completedFrom: query.completedFrom,
        completedTo: query.completedTo,
        page: query.page || 1,
        pageSize: query.pageSize || EQUIPMENT_DEFAULT_PAGE_SIZE,
        sortBy: query.sortBy || "createdAtUtc",
        sortDirection: query.sortDirection || EQUIPMENT_DEFAULT_SORT_DIRECTION,
    });

/**
 * One call for both views. `equipmentId` picks the nested route so a single-unit history and the
 * cross-unit list cannot answer differently for the same filters.
 */
export const listServiceRecords = async (equipmentId, query = {}) => {
    const path = equipmentId
        ? `${EQUIPMENT_BASE}/${equipmentId}/service-records`
        : `${EQUIPMENT_BASE}/service-records`;
    const result = await apiClient.get(path, { params: buildServiceRecordListParams(query) });
    return {
        items: result?.items || [],
        page: result?.page || 1,
        pageSize: result?.pageSize || EQUIPMENT_DEFAULT_PAGE_SIZE,
        totalCount: result?.totalCount || 0,
        totalPages: result?.totalPages || 0,
    };
};

export const getServiceRecord = async (equipmentId, recordId) =>
    apiClient.get(`${EQUIPMENT_BASE}/${equipmentId}/service-records/${recordId}`);

export const createServiceRecord = async (equipmentId, payload) =>
    apiClient.post(`${EQUIPMENT_BASE}/${equipmentId}/service-records`, payload);

export const updateServiceRecord = async (equipmentId, recordId, payload) =>
    apiClient.put(`${EQUIPMENT_BASE}/${equipmentId}/service-records/${recordId}`, payload);

/**
 * `releaseFromSessionId` names the inspection session currently holding the unit and ends that
 * occupancy in the same transaction as the start (design 4.2). Omitting it against an occupied unit
 * answers `409 EquipmentInUse` with `details.sessions` listing the sessions to choose from — naming
 * one is the confirmation, so it must come from the user rather than be filled in automatically.
 */
export const startServiceRecord = async (equipmentId, recordId, startedDate, releaseFromSessionId) =>
    apiClient.post(`${EQUIPMENT_BASE}/${equipmentId}/service-records/${recordId}/start`, {
        startedDate: startedDate || null,
        releaseFromSessionId: releaseFromSessionId || null,
    });

/** Sessions named by a `409 EquipmentInUse` body, or an empty list when the error is a different one. */
export const occupyingSessionsFromError = (error) =>
    (equipmentErrorCode(error) === equipmentErrorCodes.EquipmentInUse && error?.details?.sessions) || [];

/**
 * Completion carries only `recordVersion`. The equipment's own version is read and bumped by the
 * server inside the transaction, so a list page does not need a fresh equipment token to close a
 * work order.
 */
export const completeServiceRecord = async (equipmentId, recordId, payload) =>
    apiClient.post(`${EQUIPMENT_BASE}/${equipmentId}/service-records/${recordId}/complete`, payload);

export const cancelServiceRecord = async (equipmentId, recordId, reason) =>
    apiClient.post(`${EQUIPMENT_BASE}/${equipmentId}/service-records/${recordId}/cancel`, { reason });

export const voidServiceRecord = async (equipmentId, recordId, reason) =>
    apiClient.post(`${EQUIPMENT_BASE}/${equipmentId}/service-records/${recordId}/void`, { reason });

// --------------------------------------------------------------- attachments

export const attachmentCategoryOptions = [
    { value: "photo", label: "照片" },
    { value: "warranty", label: "保修单" },
    { value: "invoice", label: "发票" },
    { value: "service_report", label: "维修报告" },
    { value: "other", label: "其他" },
];

export const attachmentCategoryDisplay = toDisplay(attachmentCategoryOptions);
export const attachmentCategoryLabel = (code) => attachmentCategoryDisplay[code] || code || "";

/**
 * Extensions the server will accept (design 3.3). Used only to set the file picker's filter —
 * the server re-checks the extension, the magic number and, for text, the encoding, because a
 * picker filter is a convenience and not a control.
 */
export const ATTACHMENT_ACCEPT = ".jpg,.jpeg,.png,.webp,.pdf,.txt";

/** Business limit; the transport limits above it are set in Program.cs and in nginx. */
export const ATTACHMENT_MAX_BYTES = 50 * 1024 * 1024;

export const formatFileSize = (bytes) => {
    const value = Number(bytes);
    if (!Number.isFinite(value) || value < 0) {
        return "-";
    }
    if (value < 1024) {
        return `${value} B`;
    }
    if (value < 1024 * 1024) {
        return `${(value / 1024).toFixed(1)} KB`;
    }
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Server-side paged, like every other list in this module. `serviceRecordId` omitted means the
 * equipment's own files — the server reads that as "master record only", not "all of them".
 */
export const listEquipmentAttachments = async (equipmentId, { serviceRecordId, page, pageSize } = {}) => {
    const result = await apiClient.get(`${EQUIPMENT_BASE}/${equipmentId}/attachments`, {
        params: cleanParams({
            serviceRecordId,
            page: page || 1,
            pageSize: pageSize || EQUIPMENT_DEFAULT_PAGE_SIZE,
        }),
    });
    return {
        items: result?.items || [],
        page: result?.page || 1,
        pageSize: result?.pageSize || EQUIPMENT_DEFAULT_PAGE_SIZE,
        totalCount: result?.totalCount || 0,
        totalPages: result?.totalPages || 0,
    };
};

export const uploadEquipmentAttachment = async (equipmentId, { file, category, serviceRecordId }) => {
    const form = new FormData();
    form.append("file", file);
    form.append("category", category);

    const path = serviceRecordId
        ? `${EQUIPMENT_BASE}/${equipmentId}/service-records/${serviceRecordId}/attachments`
        : `${EQUIPMENT_BASE}/${equipmentId}/attachments`;

    return apiClient.upload(path, form);
};

/**
 * The API streams the file itself rather than redirecting to a pre-signed URL, so the download
 * carries the bearer token like any other request and arrives as a blob.
 */
export const downloadEquipmentAttachment = async (equipmentId, attachment) => {
    const blob = await apiClient.get(
        `${EQUIPMENT_BASE}/${equipmentId}/attachments/${attachment.id}/download`,
        { responseType: "blob" }
    );

    const url = window.URL.createObjectURL(blob);
    try {
        const link = document.createElement("a");
        link.href = url;
        link.download = attachment.originalFileName || "attachment";
        document.body.appendChild(link);
        link.click();
        link.remove();
    } finally {
        // Released on the next tick so the click has already been handed to the browser.
        window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
    }
};

export const deleteEquipmentAttachment = async (equipmentId, attachmentId) =>
    apiClient.delete(`${EQUIPMENT_BASE}/${equipmentId}/attachments/${attachmentId}`);

// ----------------------------------------------------- inspection session kit

const SESSION_BASE = "/inspectionsession";

/**
 * What a unit did on a job. Same vocabulary as the equipment type; kept separate because a unit is
 * occasionally recorded off-label in back-filled history.
 */
export const usageRoleOptions = equipmentTypeOptions;
export const usageRoleLabel = equipmentTypeLabel;

export const sessionSetupStatusOptions = [
    { value: "pending", label: "待补充" },
    { value: "configured", label: "已登记" },
    { value: "released", label: "已释放" },
    { value: "not_required", label: "无需器材" },
];

export const sessionSetupStatusDisplay = toDisplay(sessionSetupStatusOptions);
export const sessionSetupStatusLabel = (code) => sessionSetupStatusDisplay[code] || code || "";

/**
 * What this session may pick from. Deliberately not `getEquipmentOptions`, which offers only
 * `available` units: that hides both the units this job already holds and the ones another job
 * holds, and the latter are exactly the ones the re-assignment confirmation exists for — filtering
 * them out makes that flow unreachable from the UI. The tenant comes from the session, so no
 * tenantId is passed.
 */
export const getSessionEquipmentOptions = async (sessionId) => {
    const items = await apiClient.get(`${SESSION_BASE}/${sessionId}/equipment/options`);
    return items || [];
};

/**
 * The kit and its snapshots. Every "may I" flag in the response is computed server-side and is
 * rendered as given — re-deriving any of it here is how a menu ends up full of options that answer
 * 403.
 */
export const getSessionEquipment = async (sessionId) =>
    apiClient.get(`${SESSION_BASE}/${sessionId}/equipment`);

/**
 * Whole-set save. The server works out what was added, removed and kept, so the page must send the
 * complete kit rather than a sequence of edits — a half-applied change is worse than a rejected one.
 *
 * `confirmReassign` re-sends the same set after the user has agreed to take units off another job;
 * without it the server answers `ReassignmentRequiresConfirmation` and names the sessions holding
 * them.
 */
export const saveSessionEquipment = async (sessionId, { items, confirmReassign, reason } = {}) =>
    apiClient.put(`${SESSION_BASE}/${sessionId}/equipment`, {
        items: (items || []).map((item) => ({
            equipmentId: Number(item.equipmentId),
            usageRole: item.usageRole || null,
            maintenanceOverrideReason: item.maintenanceOverrideReason || null,
        })),
        confirmReassign: Boolean(confirmReassign),
        reason: reason || null,
    });

/** Omit `equipmentIds` to hand back everything still out on this job. */
export const releaseSessionEquipment = async (sessionId, { equipmentIds, reason } = {}) =>
    apiClient.post(`${SESSION_BASE}/${sessionId}/equipment/release`, {
        equipmentIds: equipmentIds && equipmentIds.length ? equipmentIds.map(Number) : null,
        reason: reason || null,
    });

export const markSessionEquipmentNotRequired = async (sessionId, reason) =>
    apiClient.post(`${SESSION_BASE}/${sessionId}/equipment/not-required`, { reason });

/** Where a single unit has been. Server-side paged like every other list in this module. */
export const listEquipmentUsageHistory = async (equipmentId, { page, pageSize } = {}) => {
    const result = await apiClient.get(`${EQUIPMENT_BASE}/${equipmentId}/inspection-sessions`, {
        params: cleanParams({
            page: page || 1,
            pageSize: pageSize || EQUIPMENT_DEFAULT_PAGE_SIZE,
        }),
    });
    return {
        items: result?.items || [],
        page: result?.page || 1,
        pageSize: result?.pageSize || EQUIPMENT_DEFAULT_PAGE_SIZE,
        totalCount: result?.totalCount || 0,
        totalPages: result?.totalPages || 0,
    };
};
